/**
 * A palette brush, drawn as the tile it paints.
 *
 * Every chip in the editor's palette carries a small picture of its glyph, and
 * that picture is not an icon set of its own: it is `drawStation` — the function
 * the round, the replay and the editor's preview all paint tiles with — called
 * on a station built the way a fresh round would build it. A spawn digit is the
 * floor with that seat's chef standing on it, drawn by `drawPlayer`. So a brush
 * looks like what it paints, and a fixture redrawn in `icons.ts` is redrawn in
 * the palette without anybody touching this file.
 *
 * The glyphs come from the engine's `MAP_LEGEND` and `CRATE_CONTENTS` via
 * `PALETTE`; nothing here knows a station type by name beyond what a fresh
 * round puts on it (plates on the stack, the extinguisher in its rack).
 */

import { CRATE_CONTENTS, MAP_LEGEND, equipmentForRack } from '@overcooked/engine'
import type { Content, EditorContent } from '@overcooked/engine'
import type { Player, Station, StationType } from '@overcooked/shared'
import type { Catalog } from '../render/catalog'
import { drawPlayer } from '../render/players'
import { drawStation } from '../render/stations'
import { PREVIEW_PLATES } from './preview'

/** CSS pixels per side of a palette swatch. */
export const SWATCH_SIZE = 40

const SPAWN = /^[1-9]$/

/** What a swatch needs to know about the content, beyond the glyph. */
export interface SwatchContext {
  /** The equipment a rack starts holding, or `null` for an empty rack. */
  rackItem: string | null
}

/**
 * The equipment a rack of this type starts a round holding — the engine's own
 * answer, from the recipe book the editor already has.
 */
export function rackItemFor(base: Pick<EditorContent, 'recipes'> | null, type: StationType): string | null {
  if (!base) return null
  return equipmentForRack({ recipes: base.recipes } as unknown as Content, type)
}

/** A station with nothing on it and nothing happening, at the origin. */
function bareStation(type: StationType): Station {
  return {
    type,
    cell: { x: 0, y: 0 },
    item: null,
    progressTicks: 0,
    progressTargetTicks: 0,
    resumedAtTick: null,
    heldBy: null,
    heldSubject: null,
    container: null,
    onFire: false,
    dispenses: null,
    cleanPlates: 0,
    dirtyPlates: 0,
  }
}

/**
 * The station a glyph puts in the kitchen, as an unstarted round has it.
 *
 * `null` for a glyph that is not a tile (a spawn digit, or a letter the legend
 * does not know). Spawn digits stand on the floor, so `swatchStation('1')` is
 * not asked for — `drawGlyphSwatch` handles them.
 */
export function swatchStation(glyph: string, context: SwatchContext): Station | null {
  const type = MAP_LEGEND[glyph]
  if (!type) return null
  const station = bareStation(type)
  station.dispenses = CRATE_CONTENTS[glyph] ?? null
  if (type === 'plate_stack') station.cleanPlates = PREVIEW_PLATES
  if (context.rackItem && type === 'extinguisher_rack') {
    station.item = { type: 'equipment', id: context.rackItem }
  }
  return station
}

/** The chef a spawn digit seats, standing still and facing the reader. */
function swatchPlayer(seat: number): Player {
  return {
    id: `swatch-${seat}`,
    seat,
    cell: { x: 0, y: 0 },
    facing: 'down',
    holding: null,
    connected: true,
  }
}

/**
 * Paint one brush into a `size`-pixel square at the origin, held still at
 * tick 0. Returns `false`, having drawn nothing, for a glyph that is not one.
 */
export function drawGlyphSwatch(
  ctx: CanvasRenderingContext2D,
  glyph: string,
  size: number,
  catalog: Catalog,
  context: SwatchContext,
): boolean {
  const box = { x: 0, y: 0, size }
  if (SPAWN.test(glyph)) {
    drawStation(ctx, bareStation('floor'), box, 0, catalog)
    drawPlayer(ctx, swatchPlayer(Number(glyph)), { cx: size / 2, cy: size / 2, tile: size }, false, catalog, 0)
    return true
  }
  const station = swatchStation(glyph, context)
  if (!station) return false
  drawStation(ctx, station, box, 0, catalog)
  return true
}
